import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Send, MapPin, Mail, Instagram } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    service_type: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData((prev) => ({ ...prev, service_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        language,
      });
      toast.success(t('contact.success'));
      setFormData({
        name: '',
        email: '',
        service_type: '',
        message: '',
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(t('contact.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const serviceOptions = [
    { value: 'closet', label: t('contact.serviceOptions.closet') },
    { value: 'decluttering', label: t('contact.serviceOptions.decluttering') },
    { value: 'newborn', label: t('contact.serviceOptions.newborn') },
    { value: 'rooms', label: t('contact.serviceOptions.rooms') },
    { value: 'unpacking', label: t('contact.serviceOptions.unpacking') },
    { value: 'workshops', label: t('contact.serviceOptions.workshops') },
    { value: 'consultation', label: t('contact.serviceOptions.consultation') },
  ];

  return (
    <main data-testid="contact-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-[#F2F0E9]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <span className="font-accent text-lg text-[#8DA399]">
            {t('contact.subtitle')}
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-[#2C3333] mt-4">
            {t('contact.title')}
          </h1>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 md:py-24 bg-[#FAFAF9]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
            {/* Form */}
            <div className="bg-[#F2F0E9] p-8 md:p-12">
              <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
                {/* Name */}
                <div className="space-y-2">
                  <Label htmlFor="name" className="font-sans text-[#2C3333]">
                    {t('contact.name')}
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="contact-name-input"
                    className="bg-white border-[#E5E5E0] focus:border-[#8DA399] focus:ring-[#8DA399]"
                    placeholder={language === 'en' ? 'Your full name' : 'Tu nombre completo'}
                  />
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="font-sans text-[#2C3333]">
                    {t('contact.email')}
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="contact-email-input"
                    className="bg-white border-[#E5E5E0] focus:border-[#8DA399] focus:ring-[#8DA399]"
                    placeholder={language === 'en' ? 'your@email.com' : 'tu@email.com'}
                  />
                </div>

                {/* Service Type */}
                <div className="space-y-2">
                  <Label htmlFor="service" className="font-sans text-[#2C3333]">
                    {t('contact.service')}
                  </Label>
                  <Select
                    value={formData.service_type}
                    onValueChange={handleServiceChange}
                    required
                  >
                    <SelectTrigger
                      data-testid="contact-service-select"
                      className="bg-white border-[#E5E5E0] focus:border-[#8DA399] focus:ring-[#8DA399]"
                    >
                      <SelectValue placeholder={t('contact.selectService')} />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceOptions.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value}
                          data-testid={`service-option-${option.value}`}
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Message */}
                <div className="space-y-2">
                  <Label htmlFor="message" className="font-sans text-[#2C3333]">
                    {t('contact.message')}
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    data-testid="contact-message-input"
                    className="bg-white border-[#E5E5E0] focus:border-[#8DA399] focus:ring-[#8DA399] min-h-[150px]"
                    placeholder={t('contact.messagePlaceholder')}
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="contact-submit-button"
                  className="w-full bg-[#8DA399] text-white hover:bg-[#7A8F85] rounded-full px-8 py-6 text-lg disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      {t('contact.sending')}
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      {t('contact.submit')}
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="flex flex-col justify-center">
              <div className="space-y-8">
                <div>
                  <h2 className="font-serif text-2xl md:text-3xl text-[#2C3333] mb-6">
                    {language === 'en' ? 'Let\'s Connect' : 'Conectemos'}
                  </h2>
                  <p className="font-sans text-lg text-[#5C5C5C] leading-relaxed">
                    {language === 'en'
                      ? 'We\'d love to hear from you. Whether you have a question about our services, want to schedule a consultation, or simply want to learn more about how we can help transform your space, we\'re here for you.'
                      : 'Nos encantaría saber de ti. Ya sea que tengas una pregunta sobre nuestros servicios, quieras programar una consulta, o simplemente quieras saber más sobre cómo podemos ayudarte a transformar tu espacio, estamos aquí para ti.'}
                  </p>
                </div>

                <div className="space-y-6">
                  <a
                    href="https://www.instagram.com/galene_home/"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-instagram-link"
                    className="flex items-center space-x-4 group"
                  >
                    <div className="p-3 bg-[#F2F0E9] group-hover:bg-[#8DA399]/10 transition-colors rounded-full">
                      <Instagram className="w-6 h-6 text-[#8DA399]" />
                    </div>
                    <div>
                      <p className="font-sans font-medium text-[#2C3333] group-hover:text-[#8DA399] transition-colors">
                        @galene_home
                      </p>
                      <p className="font-sans text-sm text-[#5C5C5C]">
                        {language === 'en' ? 'Follow us on Instagram' : 'Síguenos en Instagram'}
                      </p>
                    </div>
                  </a>

                  <div className="flex items-center space-x-4">
                    <div className="p-3 bg-[#F2F0E9] rounded-full">
                      <MapPin className="w-6 h-6 text-[#8DA399]" />
                    </div>
                    <div>
                      <p className="font-sans font-medium text-[#2C3333]">Spain</p>
                      <p className="font-sans text-sm text-[#5C5C5C]">
                        {language === 'en' ? 'Serving clients nationwide' : 'Atendiendo clientes en todo el país'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    <div className="p-3 bg-[#F2F0E9] rounded-full">
                      <Mail className="w-6 h-6 text-[#8DA399]" />
                    </div>
                    <div>
                      <p className="font-sans font-medium text-[#2C3333]">
                        {language === 'en' ? 'Contact Form' : 'Formulario de Contacto'}
                      </p>
                      <p className="font-sans text-sm text-[#5C5C5C]">
                        {language === 'en' ? 'Fill out the form and we\'ll respond within 24-48 hours' : 'Completa el formulario y responderemos en 24-48 horas'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Certifications */}
                <div className="pt-8 border-t border-[#E5E5E0]">
                  <p className="font-accent text-sm text-[#5C5C5C] italic">
                    {language === 'en'
                      ? 'Certified Professional Organizer - The Home Academy (NAPO Member School)'
                      : 'Organizador Profesional Certificado - The Home Academy (Escuela Miembro de NAPO)'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
